import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ConsolidationActivationDatesResult,
  ConsolidationBlueprintResult,
  ConsolidationPriority,
  ConsolidationPriorityStatusItem,
  ConsolidationRenderResult,
  ConsolidationView,
  ContainerChangeDetail,
  ContainerRenderResult,
  CreateConsolidationPriorityParams,
  GetContainerChangeDetailParams,
  SubmitConsolidationFeedbackParams,
  UpdateConsolidationPriorityParams,
} from "@/models/consolidation";
import RestResponse, { restError } from "@/models/client/RestResponse";
import { PATH_KEYS } from "@/services/appServices";
import { DEFAULT_CONSOLIDATION_VIEW } from "@/services/consolidationService";
import { logger } from "@/lib/logger";
import { useItemService } from "./useServices";

const unwrapConsolidationResponse = <T>(
  response: Awaited<RestResponse<T>>
): T => {
  if (response.success) return response.value;
  throw restError(response);
};

export const useConsolidationRender = (
  lawExternalId: string | undefined,
  asOf: string | undefined,
  view: ConsolidationView = DEFAULT_CONSOLIDATION_VIEW
) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useQuery<ConsolidationRenderResult>({
    queryKey: [PATH_KEYS.consolidation, "render", lawExternalId, asOf, view],
    queryFn: async ({ signal }) => {
      const response = await consolidationService.render(
        {
          lawExternalId: lawExternalId!,
          asOf: asOf!,
          view,
        },
        signal
      );
      return unwrapConsolidationResponse(response);
    },
    enabled: Boolean(lawExternalId?.trim() && asOf?.trim()),
  });
};

export const useConsolidationRenderByChangeLaw = (
  lawExternalId: string | undefined,
  changeLaw: string | undefined,
  view: ConsolidationView = DEFAULT_CONSOLIDATION_VIEW
) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useQuery<ConsolidationRenderResult>({
    queryKey: [
      PATH_KEYS.consolidation,
      "render-by-change-law",
      lawExternalId,
      changeLaw,
      view,
    ],
    queryFn: async ({ signal }) => {
      const response = await consolidationService.renderByChangeLaw(
        {
          lawExternalId: lawExternalId!,
          changeLaw: changeLaw!,
          view,
        },
        signal
      );
      return unwrapConsolidationResponse(response);
    },
    enabled: Boolean(lawExternalId?.trim() && changeLaw?.trim()),
  });
};

export const useConsolidationContainers = (
  lawExternalId: string | undefined,
  asOf: string | undefined,
  view: ConsolidationView = DEFAULT_CONSOLIDATION_VIEW
) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useQuery<ContainerRenderResult[]>({
    queryKey: [PATH_KEYS.consolidation, "containers", lawExternalId, asOf, view],
    queryFn: async ({ signal }) => {
      const response = await consolidationService.getContainers(
        {
          lawExternalId: lawExternalId!,
          asOf: asOf!,
          view,
        },
        signal
      );
      return unwrapConsolidationResponse(response);
    },
    enabled: Boolean(lawExternalId?.trim() && asOf?.trim()),
  });
};

export const useConsolidationBlueprint = (
  lawExternalId: string | undefined
) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useQuery<ConsolidationBlueprintResult>({
    queryKey: [PATH_KEYS.consolidation, "blueprint", lawExternalId],
    queryFn: async ({ signal }) => {
      const response = await consolidationService.getBlueprint(
        lawExternalId!,
        signal
      );
      return unwrapConsolidationResponse(response);
    },
    enabled: Boolean(lawExternalId?.trim()),
  });
};

/**
 * Distinct activation dates of the actively viewed consolidation, sorted
 * ascending. Scoped to the change law when one is selected; unscoped (all
 * dates in the anchor window) only for plain `as_of` QA views. Drives the
 * "version:" dropdown in the viewer toolbar.
 */
export const useConsolidationActivationDates = (
  lawExternalId: string | undefined,
  changeLaw?: string
) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useQuery<ConsolidationActivationDatesResult>({
    queryKey: [
      PATH_KEYS.consolidation,
      "activation-dates",
      lawExternalId,
      changeLaw,
    ],
    queryFn: async ({ signal }) => {
      const response = await consolidationService.getActivationDates(
        { lawExternalId: lawExternalId!, changeLaw },
        signal
      );
      return unwrapConsolidationResponse(response);
    },
    enabled: Boolean(lawExternalId?.trim()),
    staleTime: 5 * 60 * 1000,
  });
};

export const useConsolidationFeedback = () => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useMutation<void, Error, SubmitConsolidationFeedbackParams>({
    mutationKey: [PATH_KEYS.consolidation, "submit-feedback"],
    mutationFn: async (params) => {
      const response = await consolidationService.submitFeedback(params);
      if (!response.success) throw restError(response);
    },
    onError: (error, params) => {
      logger.error("Failed to submit consolidation feedback", {
        operation: "submitConsolidationFeedback",
        error,
        lawExternalId: params.lawExternalId,
        containerId: params.containerId,
        asOf: params.asOf,
        view: params.view,
      });
    },
  });
};

const PRIORITIES_QUERY_KEY = [PATH_KEYS.consolidation, "priorities"] as const;
// Prefix key for every per-page status batch (see
// useConsolidationPriorityStatuses); mutations invalidate it so an edit's new
// scope/level shows immediately instead of after the 30s poll.
const PRIORITY_STATUSES_QUERY_KEY = [
  PATH_KEYS.consolidation,
  "priority-statuses",
] as const;

/**
 * Active consolidation prioritizations for the caller's tenant hierarchy —
 * WITHOUT status rollups: those are expensive server-side and are fetched per
 * visible page via useConsolidationPriorityStatuses.
 */
export const useConsolidationPriorities = (enabled = true) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useQuery<ConsolidationPriority[]>({
    queryKey: PRIORITIES_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await consolidationService.getPriorities(signal);
      return unwrapConsolidationResponse(response);
    },
    enabled,
  });
};

/**
 * Status rollups for the given prioritizations (the visible table page).
 * Refetches every 30s so status badges follow the urgent poller's progress
 * while the admin view is open.
 */
export const useConsolidationPriorityStatuses = (
  ids: string[],
  enabled = true
) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useQuery<ConsolidationPriorityStatusItem[]>({
    queryKey: [...PRIORITY_STATUSES_QUERY_KEY, ids.join(",")],
    queryFn: async ({ signal }) => {
      const response = await consolidationService.getPriorityStatuses(
        ids,
        signal
      );
      return unwrapConsolidationResponse(response);
    },
    refetchInterval: 30_000,
    enabled: enabled && ids.length > 0,
  });
};

export const useCreateConsolidationPriority = () => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );
  const queryClient = useQueryClient();

  return useMutation<
    ConsolidationPriority,
    Error,
    CreateConsolidationPriorityParams
  >({
    mutationKey: [PATH_KEYS.consolidation, "create-priority"],
    mutationFn: async (params) => {
      const response = await consolidationService.createPriority(params);
      return unwrapConsolidationResponse(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRIORITIES_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: PRIORITY_STATUSES_QUERY_KEY,
      });
    },
    onError: (error, params) => {
      logger.error("Failed to create consolidation priority", {
        operation: "createConsolidationPriority",
        error,
        lawExternalId: params.lawExternalId,
        pivotExternalId: params.pivotExternalId,
        mode: params.mode,
      });
    },
  });
};

export const useUpdateConsolidationPriority = () => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateConsolidationPriorityParams>({
    mutationKey: [PATH_KEYS.consolidation, "update-priority"],
    mutationFn: async (params) => {
      const response = await consolidationService.updatePriority(params);
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRIORITIES_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: PRIORITY_STATUSES_QUERY_KEY,
      });
    },
    onError: (error, params) => {
      logger.error("Failed to update consolidation priority", {
        operation: "updateConsolidationPriority",
        error,
        priorityId: params.id,
        pivotExternalId: params.pivotExternalId,
        mode: params.mode,
      });
    },
  });
};

export const useRevokeConsolidationPriority = () => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationKey: [PATH_KEYS.consolidation, "revoke-priority"],
    mutationFn: async (id) => {
      const response = await consolidationService.revokePriority(id);
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PRIORITIES_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: PRIORITY_STATUSES_QUERY_KEY,
      });
    },
    onError: (error, id) => {
      logger.error("Failed to revoke consolidation priority", {
        operation: "revokeConsolidationPriority",
        error,
        priorityId: id,
      });
    },
  });
};

export const useConsolidationContainerChanges = (
  params: GetContainerChangeDetailParams | null
) => {
  const consolidationService = useItemService(
    (services) => services.consolidation
  );

  return useQuery<ContainerChangeDetail[]>({
    queryKey: [
      PATH_KEYS.consolidation,
      "container-changes",
      params?.lawExternalId,
      params?.containerId,
      params?.asOf,
      params?.view,
      params?.changeLaw,
    ],
    queryFn: async ({ signal }) => {
      const response = await consolidationService.getContainerChanges(
        params!,
        signal
      );
      return unwrapConsolidationResponse(response);
    },
    enabled: Boolean(
      params?.lawExternalId?.trim() &&
        params?.containerId?.trim() &&
        params?.asOf?.trim()
    ),
  });
};
